import React, {useCallback} from 'react';
import {applyNodeChanges} from 'reactflow';

import {FlowchartNode} from '../processFlowchartConfigEditorNodeTypes';
import {
  FlowchartEdgeChange,
  FlowchartNodeChange,
  FlowchartNodeChangeType,
} from '../processFlowchartConfigEditorUtils';
import {ApplyFlowchartChangesFn} from './FlowchartStateTypes';

interface UseReactFlowChangesOnlyParams {
  applyFlowchartChanges: ApplyFlowchartChangesFn;
  setInteractiveNodes: React.Dispatch<React.SetStateAction<Array<FlowchartNode>>>;
  setNodePositions: React.Dispatch<React.SetStateAction<Record<string, {x: number; y: number}>>>;
  currentNodePositionsRef: React.MutableRefObject<Record<string, {x: number; y: number}>>;
}

/**
 * Returns ReactFlow-specific change handlers. Node position changes are handled
 * locally (avoiding Redux round-trips during drag operations); other changes
 * are forwarded to `applyFlowchartChanges`.
 */
export function useReactFlowChangesOnly({
  applyFlowchartChanges,
  setInteractiveNodes,
  setNodePositions,
  currentNodePositionsRef,
}: UseReactFlowChangesOnlyParams): {
  onReactFlowNodeChangesOnly: (nodeChanges: Array<FlowchartNodeChange>) => void;
  onReactFlowEdgeChangesOnly: (edgeChanges: Array<FlowchartEdgeChange>) => void;
} {
  const onReactFlowNodeChangesOnly = useCallback(
    (nodeChanges: Array<FlowchartNodeChange>) => {
      const positionChanges = nodeChanges.filter((c) => c.type === FlowchartNodeChangeType.POSITION);
      // Dimension changes (height and width of a node) are ignored — autolayout is
      // the only code that relies on this, and dimensions don't propagate from
      // ReactFlow when the flowchart is locked. Autolayout grabs dimensions from
      // node internals on its own.
      const otherChanges = nodeChanges.filter(
        (c) => c.type !== FlowchartNodeChangeType.POSITION && c.type !== FlowchartNodeChangeType.DIMENSIONS
      );

      if (positionChanges.length > 0) {
        // Handle position changes locally using ReactFlow's applyNodeChanges.
        // This avoids triggering Redux updates and full flowchart rebuilds during dragging.
        const changedIds = new Set(positionChanges.map((c) => c.id));
        setInteractiveNodes((nodes) => {
          const updatedNodes = applyNodeChanges(positionChanges, nodes);
          for (const node of updatedNodes) {
            if (changedIds.has(node.id)) {
              currentNodePositionsRef.current[node.id] = node.position;
            }
          }
          return updatedNodes;
        });

        const positionUpdates: Record<string, {x: number; y: number}> = {};
        for (const change of positionChanges) {
          if (change.position) {
            positionUpdates[change.id] = change.position;
          }
        }
        if (Object.keys(positionUpdates).length > 0) {
          setNodePositions((prev) => ({...prev, ...positionUpdates}));
        }
      }

      if (otherChanges.length > 0) {
        applyFlowchartChanges(() => ({nodeChanges: otherChanges, edgeChanges: []}));
      }
    },
    [applyFlowchartChanges, setInteractiveNodes, setNodePositions, currentNodePositionsRef]
  );

  const onReactFlowEdgeChangesOnly = useCallback(
    (edgeChanges: Array<FlowchartEdgeChange>) => {
      applyFlowchartChanges(() => ({nodeChanges: [], edgeChanges}));
    },
    [applyFlowchartChanges]
  );

  return {onReactFlowNodeChangesOnly, onReactFlowEdgeChangesOnly};
}
